import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import { InitState } from '../../../../redux/reducers/root/initialState'
import React, { useCallback, useEffect, useMemo } from 'react'
import ObjectChooser from './ObjectChooser'
import { tracking } from '../../../../tracking/tracking'
import { configureObjectChooser } from '../../../../redux/actions/flowInformation/flowInformation'
import { DisplayObjectChooserType } from '../../../../@types/object-chooser-types'
import { resetInsertion } from '../../../../redux/reducers/insertion/insertionFeature'
import { Flow } from '../../../../@types/flow-types'
import { Trans, useTranslation } from 'react-i18next'
import { ButtonRounded, StickyFooter } from 'is24-corecss'
import styles from './ObjectChooserPage.module.css'
import { TransactionType } from '../../../../@types/types'
import { ConvertedRealEstate } from '../../../../@types/real-estate-api-types'

export default function ObjectChooserPage(props: { flow: Flow }) {
  const objects = useAppSelector((state: InitState) => state.flowFeature.objectChooserInformation.objects)
  const { flow } = props
  const transaction = flow.flowInformation.metaData.transaction

  const filteredObjects = useMemo(() => {
    return objects?.filter((o) => filterOutPublishedForCurrentTransactionType(transaction, o)) || []
  }, [objects, transaction])

  const dispatch = useAppDispatch()

  const continueToEmptyInsertionFlow = useCallback(() => {
    tracking({
      eventName: 'insert_new_object_click',
      flowInformation: flow.flowInformation,
      entryMode: 'create',
      experiment: 'object_chooser',
    })

    dispatch(configureObjectChooser(DisplayObjectChooserType.NO, undefined, true, false))
    dispatch(resetInsertion())
  }, [dispatch, flow.flowInformation])

  useEffect(() => {
    if (!filteredObjects || filteredObjects.length === 0) {
      continueToEmptyInsertionFlow()
    }
  }, [continueToEmptyInsertionFlow, filteredObjects])

  return (
    <>
      <ObjectChooser objects={filteredObjects} />
      <NewInsertionBar continueToEmptyInsertionFlow={continueToEmptyInsertionFlow} />
    </>
  )
}

interface BottomBarProps {
  continueToEmptyInsertionFlow?: () => void
}

function NewInsertionBar(props: BottomBarProps) {
  const { t } = useTranslation()

  const contentWithButton = (
    <div className='content-wrapper'>
      <div
        className='margin'
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'end',
          alignItems: 'center',
        }}
      >
        <p className={styles.newInsertionText}>
          <Trans components={{ 1: <br className='lap-hide desk-hide' /> }} i18nKey='objectChooser.newInsertionText' />
        </p>
        <ButtonRounded
          id='preview-listing-type'
          data-cy='preview-listing-type'
          variant='filledDefault'
          className='margin-left'
          onClick={props.continueToEmptyInsertionFlow}
        >
          {t('objectChooser.newInsertionButtonNew')}
        </ButtonRounded>
      </div>
    </div>
  )

  return <StickyFooter align='right' infoContent='' primaryButton={contentWithButton} show />
}

function filterOutPublishedForCurrentTransactionType(transaction: TransactionType, object: ConvertedRealEstate) {
  const notPublishedInRentFlow = transaction === TransactionType.RENT && !object.publishInfo?.rentPublished
  const notPublishedInSellFlow = transaction === TransactionType.SELL && !object.publishInfo?.sellPublished
  return notPublishedInRentFlow || notPublishedInSellFlow
}
